import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

// Comprehensive course data with rich content
const courseData = {
  "solar-design": {
    id: "solar-design",
    title: "Solar Design & Installation Mastery",
    subtitle: "Master the art of designing and installing solar systems for the Nigerian market",
    duration: "4 weeks",
    level: "Intermediate",
    instructor: "Engr. Adebayo Solar",
    lastUpdated: "December 2024",
    language: "English",
    certificate: true,
    price: "₦25,000",
    rating: 4.8,
    students: 1247,
    modules: [
      {
        id: "module-1",
        title: "Site Assessment & Solar Resource Analysis",
        duration: "1 week",
        lessons: [
          {
            id: "lesson-1-1",
            title: "Understanding Solar Radiation in Nigeria",
            duration: "45 min",
            type: "video",
            description: "Learn about Nigeria's solar potential, seasonal variations, and how to assess solar resource availability at different locations across the country."
          },
          {
            id: "lesson-1-2",
            title: "Site Survey Techniques",
            duration: "60 min",
            type: "video",
            description: "Master the essential site survey techniques including shading analysis, roof assessment, and structural evaluation for optimal solar installation."
          },
          {
            id: "lesson-1-3",
            title: "Solar Path Analysis Tools",
            duration: "30 min",
            type: "interactive",
            description: "Hands-on practice with solar path analysis software and tools to determine optimal panel placement and orientation."
          }
        ]
      },
      {
        id: "module-2",
        title: "System Sizing & Component Selection",
        duration: "1 week",
        lessons: [
          {
            id: "lesson-2-1",
            title: "Load Analysis & Energy Consumption",
            duration: "90 min",
            type: "video",
            description: "Comprehensive guide to analyzing client energy consumption patterns and determining accurate system sizing requirements."
          },
          {
            id: "lesson-2-2",
            title: "Solar Panel Selection & Comparison",
            duration: "75 min",
            type: "video",
            description: "Compare different solar panel technologies, efficiency ratings, and select the best options for Nigerian climate conditions."
          },
          {
            id: "lesson-2-3",
            title: "Inverter & Battery Selection",
            duration: "60 min",
            type: "video",
            description: "Choose the right inverters and batteries based on system requirements, budget constraints, and reliability needs."
          }
        ]
      },
      {
        id: "module-3",
        title: "Electrical Design & Safety Standards",
        duration: "1 week",
        lessons: [
          {
            id: "lesson-3-1",
            title: "Electrical System Design",
            duration: "120 min",
            type: "video",
            description: "Design electrical systems that comply with Nigerian electrical codes and international safety standards."
          },
          {
            id: "lesson-3-2",
            title: "Safety Protocols & Best Practices",
            duration: "90 min",
            type: "video",
            description: "Learn essential safety protocols for solar installation, including electrical safety, working at heights, and emergency procedures."
          },
          {
            id: "lesson-3-3",
            title: "Grounding & Protection Systems",
            duration: "60 min",
            type: "video",
            description: "Implement proper grounding and protection systems to ensure long-term system reliability and safety."
          }
        ]
      },
      {
        id: "module-4",
        title: "Installation Best Practices & Quality Control",
        duration: "1 week",
        lessons: [
          {
            id: "lesson-4-1",
            title: "Installation Planning & Execution",
            duration: "150 min",
            type: "video",
            description: "Step-by-step installation process from mounting systems to final electrical connections and system testing."
          },
          {
            id: "lesson-4-2",
            title: "Quality Control & Testing",
            duration: "90 min",
            type: "video",
            description: "Implement comprehensive quality control measures and testing procedures to ensure optimal system performance."
          },
          {
            id: "lesson-4-3",
            title: "Commissioning & Handover",
            duration: "60 min",
            type: "video",
            description: "Final commissioning procedures, client training, and professional handover documentation."
          }
        ]
      }
    ],
    learningOutcomes: [
      "Design solar systems for residential and commercial applications with confidence",
      "Calculate accurate system sizing based on Nigerian weather patterns and energy consumption",
      "Ensure compliance with local electrical codes and international safety standards",
      "Implement quality control measures for long-term system reliability and performance",
      "Master installation techniques for both grid-tied and off-grid systems",
      "Develop professional project management skills for solar installations"
    ],
    requirements: [
      "Basic understanding of electrical systems",
      "High school level mathematics",
      "Access to a computer for design software",
      "Commitment to complete all modules and assignments"
    ],
    description: `This intensive 4-week program is designed for solar professionals who want to excel in the Nigerian market. You'll learn how to assess sites for optimal solar potential, design systems that maximize energy production, and install them to the highest standards of safety and reliability.

The course includes hands-on training with real-world case studies from Nigerian installations, covering both grid-tied and off-grid systems. You'll master the technical aspects of solar design while understanding the unique challenges and opportunities in the Nigerian energy market.

By the end of this course, you'll be able to confidently design and install solar systems that not only meet your clients' energy needs but also provide excellent return on investment and long-term reliability.`,
    whatYouWillLearn: `Throughout this comprehensive course, you'll gain practical skills and theoretical knowledge essential for successful solar system design and installation in Nigeria.

**Module 1: Site Assessment & Solar Resource Analysis**
- Understand Nigeria's solar radiation patterns and seasonal variations
- Master site survey techniques including shading analysis and structural assessment
- Use professional tools for solar path analysis and optimal panel placement

**Module 2: System Sizing & Component Selection**
- Analyze client energy consumption patterns accurately
- Select appropriate solar panels, inverters, and batteries for Nigerian conditions
- Calculate system sizing based on energy requirements and budget constraints

**Module 3: Electrical Design & Safety Standards**
- Design electrical systems compliant with Nigerian codes
- Implement safety protocols for installation and maintenance
- Ensure proper grounding and protection systems

**Module 4: Installation Best Practices & Quality Control**
- Execute professional installation procedures
- Implement quality control measures and testing protocols
- Complete commissioning and client handover processes`
  },
  "solar-basics": {
    id: "solar-basics",
    title: "Solar Energy Fundamentals",
    subtitle: "Master the fundamentals of solar energy technology and understand how it can benefit your home or business",
    duration: "2 weeks",
    level: "Beginner",
    instructor: "Dr. Fatima Energy",
    lastUpdated: "December 2024",
    language: "English",
    certificate: true,
    price: "₦15,000",
    rating: 4.9,
    students: 2156,
    modules: [
      {
        id: "module-1",
        title: "How Solar Energy Works",
        duration: "3 days",
        lessons: [
          {
            id: "lesson-1-1",
            title: "The Science Behind Solar Panels",
            duration: "30 min",
            type: "video",
            description: "Learn the fundamental physics of how sunlight is converted into electricity through photovoltaic cells."
          },
          {
            id: "lesson-1-2",
            title: "Types of Solar Technologies",
            duration: "45 min",
            type: "video",
            description: "Explore different solar technologies including monocrystalline, polycrystalline, and thin-film panels."
          },
          {
            id: "lesson-1-3",
            title: "Solar Energy vs Traditional Power",
            duration: "30 min",
            type: "interactive",
            description: "Compare solar energy with traditional power sources in terms of cost, efficiency, and environmental impact."
          }
        ]
      },
      {
        id: "module-2",
        title: "Solar Technology & Components",
        duration: "4 days",
        lessons: [
          {
            id: "lesson-2-1",
            title: "Understanding Solar System Components",
            duration: "60 min",
            type: "video",
            description: "Learn about solar panels, inverters, batteries, charge controllers, and mounting systems."
          },
          {
            id: "lesson-2-2",
            title: "How Inverters Work",
            duration: "45 min",
            type: "video",
            description: "Understand the role of inverters in converting DC power to AC power for home and business use."
          },
          {
            id: "lesson-2-3",
            title: "Battery Storage Systems",
            duration: "60 min",
            type: "video",
            description: "Explore different battery technologies and their applications in solar energy storage."
          }
        ]
      },
      {
        id: "module-3",
        title: "Nigerian Energy Landscape & Solar Potential",
        duration: "3 days",
        lessons: [
          {
            id: "lesson-3-1",
            title: "Nigeria's Energy Challenges",
            duration: "45 min",
            type: "video",
            description: "Understand Nigeria's current energy situation and the role solar can play in addressing power challenges."
          },
          {
            id: "lesson-3-2",
            title: "Solar Potential Across Nigeria",
            duration: "60 min",
            type: "video",
            description: "Explore solar radiation patterns across different regions of Nigeria and their implications for solar adoption."
          },
          {
            id: "lesson-3-3",
            title: "Government Policies & Incentives",
            duration: "30 min",
            type: "video",
            description: "Learn about current government policies, incentives, and regulations affecting solar adoption in Nigeria."
          }
        ]
      },
      {
        id: "module-4",
        title: "Environmental & Economic Benefits",
        duration: "4 days",
        lessons: [
          {
            id: "lesson-4-1",
            title: "Environmental Impact of Solar Energy",
            duration: "45 min",
            type: "video",
            description: "Understand how solar energy reduces carbon emissions and contributes to environmental sustainability."
          },
          {
            id: "lesson-4-2",
            title: "Economic Benefits & Cost Savings",
            duration: "60 min",
            type: "video",
            description: "Calculate potential cost savings and return on investment for solar energy systems."
          },
          {
            id: "lesson-4-3",
            title: "Long-term Value & Property Enhancement",
            duration: "30 min",
            type: "video",
            description: "Learn how solar installations can increase property value and provide long-term financial benefits."
          }
        ]
      }
    ],
    learningOutcomes: [
      "Understand the science behind solar energy generation and conversion",
      "Identify and explain the key components of solar energy systems",
      "Recognize Nigeria's solar potential and current energy challenges",
      "Evaluate the environmental and economic benefits of solar adoption",
      "Make informed decisions about solar energy for your home or business",
      "Understand government policies and incentives for solar adoption"
    ],
    requirements: [
      "No prior technical knowledge required",
      "Basic computer skills for online learning",
      "Interest in renewable energy and sustainability",
      "Commitment to complete all course materials"
    ],
    description: `Solar energy is transforming how we power our homes and businesses, but understanding how it works is crucial for making informed decisions. This comprehensive course breaks down the complex world of solar energy into easy-to-understand concepts, specifically tailored for the Nigerian context.

You'll learn about the science behind solar panels, how they convert sunlight into electricity, and why Nigeria is particularly well-suited for solar energy adoption. The course explores the country's abundant solar resources and how they can help address energy challenges while providing significant cost savings.

By understanding the fundamentals, you'll be better equipped to evaluate solar options, ask the right questions, and make confident decisions about solar adoption for your home or business.`,
    whatYouWillLearn: `This beginner-friendly course will take you from complete novice to solar energy expert, covering all the essential knowledge you need to understand and evaluate solar energy options.

**Module 1: How Solar Energy Works**
- Learn the fundamental physics of photovoltaic energy conversion
- Understand different solar panel technologies and their applications
- Compare solar energy with traditional power sources

**Module 2: Solar Technology & Components**
- Master the function of each component in a solar energy system
- Understand how inverters convert and manage electrical power
- Explore battery storage options and their applications

**Module 3: Nigerian Energy Landscape & Solar Potential**
- Analyze Nigeria's current energy challenges and opportunities
- Understand solar radiation patterns across different regions
- Learn about government policies and incentives for solar adoption

**Module 4: Environmental & Economic Benefits**
- Calculate the environmental impact of solar energy adoption
- Understand the economic benefits and cost savings potential
- Learn how solar installations enhance property value`
  }
};

const CourseView = () => {
  const { courseId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);

  const course = courseData[courseId];

  useEffect(() => {
    const loadEnrolledCourses = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setEnrolledCourses(data.enrolledCourses || []);
        }
      } catch (error) {
        console.error('Error loading enrolled courses:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnrolledCourses();
  }, [currentUser]);

  const isEnrolled = enrolledCourses.includes(courseId);

  const markLessonComplete = async (lessonId) => {
    if (!completedLessons.includes(lessonId)) {
      const newCompletedLessons = [...completedLessons, lessonId];
      setCompletedLessons(newCompletedLessons);
      
      // Save progress to Firebase
      if (currentUser) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            [`courseProgress.${courseId}.completedLessons`]: newCompletedLessons,
            updatedAt: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving progress:', error);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-abiu-green-bg min-h-screen py-10 px-4">
        <div className="max-w-6xl mx-auto w-full flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-abiu-green border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-600">Loading course...</span>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="bg-abiu-green-bg min-h-screen py-10 px-4">
        <div className="max-w-6xl mx-auto w-full text-center">
          <h1 className="text-2xl font-bold text-abiu-green-dark mb-4">Course Not Found</h1>
          <p className="text-gray-600 mb-6">The course you're looking for doesn't exist.</p>
          <Link to="/academy" className="bg-abiu-green text-white px-6 py-2 rounded-lg font-medium hover:bg-abiu-green-dark transition">
            Back to Academy
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-abiu-green-bg min-h-screen py-10 px-4">
      <main className="max-w-6xl mx-auto w-full">
        {/* Course Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Link to="/academy" className="text-abiu-green hover:text-abiu-green-dark text-sm sm:text-base">
                  ← Back to Academy
                </Link>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-abiu-green-dark mb-2">{course.title}</h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-4">{course.subtitle}</p>
              
              <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-6">
                <span>⏱️ {course.duration}</span>
                <span>📊 {course.level}</span>
                <span className="hidden sm:inline">👨‍🏫 {course.instructor}</span>
                <span>⭐ {course.rating} ({course.students})</span>
                <span className="hidden sm:inline">🔄 {course.lastUpdated}</span>
                <span className="hidden sm:inline">🌍 {course.language}</span>
                {course.certificate && <span className="hidden sm:inline">🏆 Certificate</span>}
              </div>

              {!isEnrolled && (
                <div className="bg-abiu-green-light rounded-lg p-4 mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-semibold text-abiu-green-dark text-sm sm:text-base">Enroll to access this course</p>
                      <p className="text-xs sm:text-sm text-gray-600">Get lifetime access to all course materials</p>
                    </div>
                    <div className="sm:text-right">
                      <p className="text-xl sm:text-2xl font-bold text-abiu-green-dark">{course.price}</p>
                      <p className="text-xs sm:text-sm text-gray-600">One-time payment</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Course Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-abiu-green-dark mb-4 sm:mb-6">Course Content</h2>
              
              <div className="space-y-4 sm:space-y-6">
                {course.modules.map((module, moduleIndex) => (
                  <div key={module.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div 
                      className="bg-gray-50 p-3 sm:p-4 cursor-pointer hover:bg-gray-100 transition"
                      onClick={() => setActiveModule(activeModule === moduleIndex ? -1 : moduleIndex)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-abiu-green-dark text-sm sm:text-base">
                            Module {moduleIndex + 1}: {module.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600">{module.duration}</p>
                        </div>
                        <span className="text-abiu-green ml-2 flex-shrink-0">
                          {activeModule === moduleIndex ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                    
                    {activeModule === moduleIndex && (
                      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <div key={lesson.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-abiu-green-bg rounded-lg gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <span className="text-abiu-green text-lg flex-shrink-0 mt-0.5">
                                {completedLessons.includes(lesson.id) ? '✓' : '○'}
                              </span>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-abiu-green-dark text-sm sm:text-base">
                                  {lessonIndex + 1}. {lesson.title}
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-600 mt-1">{lesson.description}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-gray-500">⏱️ {lesson.duration}</span>
                                  <span className="text-xs text-gray-500">📹 {lesson.type}</span>
                                </div>
                              </div>
                            </div>
                            {isEnrolled && (
                              <button
                                onClick={() => markLessonComplete(lesson.id)}
                                className="text-abiu-green hover:text-abiu-green-dark text-xs sm:text-sm font-medium self-start sm:self-center"
                              >
                                {completedLessons.includes(lesson.id) ? 'Completed' : 'Mark Complete'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* What You'll Learn */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-abiu-green-dark mb-4 sm:mb-6">What You'll Learn</h2>
              <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                {course.learningOutcomes.map((outcome, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-abiu-green text-lg mt-0.5 flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm sm:text-base">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-abiu-green-dark mb-4 sm:mb-6">Requirements</h2>
              <ul className="space-y-3">
                {course.requirements.map((requirement, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-gray-400 text-lg mt-0.5 flex-shrink-0">•</span>
                    <span className="text-gray-700 text-sm sm:text-base">{requirement}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Course Description */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-abiu-green-dark mb-4 sm:mb-6">Course Description</h2>
              <div className="prose max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-line mb-6 text-sm sm:text-base">
                  {course.description}
                </div>
                <div className="bg-abiu-green-bg rounded-lg p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-abiu-green-dark mb-3 sm:mb-4">What You'll Learn</h3>
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                    {course.whatYouWillLearn}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 sticky top-8">
              <h3 className="text-base sm:text-lg font-semibold text-abiu-green-dark mb-3 sm:mb-4">Course Progress</h3>
              
                              {isEnrolled ? (
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-abiu-green-bg rounded-lg p-3 sm:p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs sm:text-sm font-medium text-abiu-green-dark">Progress</span>
                      <span className="text-xs sm:text-sm text-gray-600">
                        {completedLessons.length} / {course.modules.reduce((total, module) => total + module.lessons.length, 0)} lessons
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-abiu-green h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(completedLessons.length / course.modules.reduce((total, module) => total + module.lessons.length, 0)) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Modules completed:</span>
                      <span className="font-medium">
                        {course.modules.filter((_, index) => 
                          course.modules[index].lessons.every(lesson => completedLessons.includes(lesson.id))
                        ).length} / {course.modules.length}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Time remaining:</span>
                      <span className="font-medium">~{Math.ceil((course.modules.reduce((total, module) => total + module.lessons.length, 0) - completedLessons.length) * 0.5)} hours</span>
                    </div>
                  </div>
                  
                  <button className="w-full bg-abiu-green text-white py-2 sm:py-3 rounded-lg font-semibold hover:bg-abiu-green-dark transition text-sm">
                    Continue Learning
                  </button>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-bold text-abiu-green-dark">{course.price}</p>
                    <p className="text-xs sm:text-sm text-gray-600">One-time payment</p>
                  </div>
                  
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="text-abiu-green">✓</span>
                      Full lifetime access
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-abiu-green">✓</span>
                      Access on mobile and TV
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-abiu-green">✓</span>
                      Certificate of completion
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-abiu-green">✓</span>
                      30-day money-back guarantee
                    </li>
                  </ul>
                  
                  <button className="w-full bg-abiu-green text-white py-2 sm:py-3 rounded-lg font-semibold hover:bg-abiu-green-dark transition text-sm">
                    Enroll Now
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Secure checkout powered by Stripe
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CourseView; 